import React, { useEffect, useState, useCallback } from "react";
import { PropTypes } from "prop-types";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardFooter,
} from "reactstrap";
import { useDropzone } from "react-dropzone";
import "./file-uploader.scss";
import styled from "styled-components";
import IconFactory from "./service/icon-factory";
import { fileListToBase64, toKilobyte, getColor } from "./service/helper";
import ImagePreviewFx from "./../image-preview-fx/image-preview-fx";
import ToolTipFx from "./../tooltip/tool-tip-fx";
import BlockUiFx from "./../block-ui-fx";

const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
`;

const FileUploader = (props) => {
  const [files, setFiles] = useState([]);
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isDropping, toggleDrop] = useState(false);
  const {
    text,
    dropText,
    minSize,
    maxSize,
    maxFiles,
    initialFiles,
    mode,
    fileTypes,
    isReadonly,
    cloudStoragePath,
    onChange,
  } = props;

  const onDrop = useCallback(async (acceptedFiles) => {
    // Reject files exceeding the maxFiles limit
    toggleDrop(true);
    let finalAcceptedFiles = [];
    let filesCount = files.length + acceptedFiles.length;
    if (filesCount > maxFiles) {
      let itemsUpperLimit = maxFiles - files.length;
      finalAcceptedFiles = acceptedFiles.slice(0, itemsUpperLimit);
    } else {
      finalAcceptedFiles = acceptedFiles;
    }

    let additionalFiles = finalAcceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    // Convert to base64 each file
    const arrayOfBase64Files = await fileListToBase64(additionalFiles);

    files.push(...arrayOfBase64Files);
    setFiles(files);

    onChange([...files]);
    toggleDrop(false);
  });

  const getFileTypesAccepted = () => {
    if (mode === "Image") return "image/*";
    if (mode === "WorkBook") return [".pdf", ".jpg", ".jpeg", ".png"].join(",");
    if (mode === "Pdf") return ".pdf";
    if (mode === "File" && fileTypes.length === 0) {
      return [
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".pdf",
        ".jpg",
        ".png",
        ".gif",
        ".bmp",
      ].join(",");
    } else {
      return fileTypes.join(",");
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    rejectedFiles,
    acceptedFiles,
  } = useDropzone({
    accept: getFileTypesAccepted(),
    minSize: minSize,
    maxSize: maxSize,
    //multiple: true,
    onDrop,
    disabled: isReadonly,
  });

  const openPreview = (file) => {
    setPreviewUrl(file.preview);
    setPreviewOpen(true);
  };

  const removeFile = useCallback((file) => () => {
    if (isReadonly) return;
    URL.revokeObjectURL(file.preview);
    let filesUpdated = files.filter((o) => o !== file);
    setFiles(filesUpdated);

    onChange([...filesUpdated]);
  });

  const closePreview = (e) => {
    setPreviewOpen(false);
  };

  const downloadFile = useCallback((file) => () => {
    const { name, type } = file;
    if (type === "new") return false;
    var a = document.createElement("a");
    a.href = `${cloudStoragePath}/${name}`;
    a.setAttribute("download", "download");
    var b = document.createEvent("MouseEvents");
    b.initEvent("click", false, true);
    a.dispatchEvent(b);
    return false;
  });

  // Refactor: Separate file
  const imageListing = files.map((file, index) => (
    <div key={index} className="fileDetails ml-3">
      <div className="thumb" key={file.name}>
        <div className="thumbInner">
          <img
            id={`remove${index}`}
            src={file.preview}
            className="img sol-clickable thumb-img"
            onClick={() => openPreview(file)}
          />
          <ToolTipFx target={`remove${index}`}>Open preview</ToolTipFx>
        </div>
      </div>
      <div className="ml-2">
        <div className="text-muted">
          <small className="font-bold">Filename</small>
        </div>
        <div className="text-muted sol-truncate-xs">
          <small>{file.name}</small>
        </div>
        <div className="text-muted">
          <small className="font-bold">Size</small>
        </div>
        <div className="text-muted">
          <small>{toKilobyte(file.size)} Kb</small>
        </div>
        <div className="mt-2">
          {!isReadonly && (
            <Button type="button" onClick={removeFile(file)}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  ));

  const limitFileName = (fileName) => {
    if (fileName.length > 18) {
      let name = fileName.substring(0, fileName.lastIndexOf("."));
      let extension = fileName.substring(fileName.lastIndexOf("."));
      return `${name.substring(0, 16)}...${extension}`;
    }
    return fileName;
  };

  // Refactor: Separate file
  const fileListing = files.map((file, index) => {
    const ext = file.name.substring(file.name.lastIndexOf(".") + 1);
    const icon = IconFactory().getIcon(ext);
    let limitedName = limitFileName(file.name);

    return (
      <Col key={index} className="my-2" xs="6" md="4">
        <Card>
          <CardBody>
            <div className="remove-button">
              <i
                id={`remove${index}`}
                className="fas fa-times sol-clickable text-muted"
                onClick={removeFile(file)}
              />
              <ToolTipFx target={`remove${index}`}>Remove</ToolTipFx>
            </div>
            <Row className="w-100 justify-content-center">
              <Col className="p-2" xs="4">
                <>
                  <i
                    id={`download${index}`}
                    className={`${icon} fa-7x sol-clickable`}
                  />
                  {file.type === "init" && (
                    <ToolTipFx target={`download${index}`}>Download</ToolTipFx>
                  )}
                </>
              </Col>
            </Row>
          </CardBody>
          <CardFooter>
            <Row>
              <Col>
                <span className="text-muted small">{limitedName}</span>
                <span className="font-weight-bold">
                  {" "}
                  ({Math.round(file.size / 1024)} kb)
                </span>
              </Col>
            </Row>
          </CardFooter>
        </Card>
      </Col>
    );
  });

  const DragMessage = ({ isDragActive, isDragReject, rejected }) => {
    return (
      <>
        <BlockUiFx blocking={isDropping} lowerZIndex={true}>
          {!isDragActive && !rejected && (
            <p
              className="mt-3 text-center drag-msg"
              style={{ userSelect: "none", pointerEvents: "none" }}
            >
              {text} Maximum of {maxFiles} file{maxFiles > 1 ? "s" : ""}.
              {files.length > 0 ? (
                <>
                  <br />
                  <span className="small text-primary">
                    <strong>{files.length}</strong> file(s) attached.
                  </span>
                </>
              ) : null}
            </p>
          )}
          {isDragActive && !rejected && (
            <p
              className="mt-3 drag-msg"
              style={{ userSelect: "none", pointerEvents: "none" }}
            >
              {dropText}
            </p>
          )}
          {rejected && (
            <p
              className="mt-3 drag-msg"
              style={{ userSelect: "none", pointerEvents: "none" }}
            >
              File type is not supported.
            </p>
          )}
        </BlockUiFx>
      </>
    );
  };

  const FileTooLarge = () => {
    const isFileTooLarge =
      rejectedFiles.length > 0 && rejectedFiles[0].size > maxSize;
    return (
      <React.Fragment>
        {isFileTooLarge && <p className="text-danger">File is too large.</p>}
      </React.Fragment>
    );
  };

  const reloadInitialFiles = () => {
    if (!initialFiles || initialFiles.length <= 0) return;
    setFiles(
      initialFiles.map((o) =>
        Object.assign(
          {},
          {
            type: "init",
            name: o.name,
            size: o.size,
            preview: getPreviewData(o),
          }
        )
      )
    );
  };

  const getPreviewData = (file) => {
    if (file.data.includes("data:")) return file.data;
    return `${cloudStoragePath}/${file.name}`;
  };

  const isImageMode = () => mode === "Image";

  // Component did mount
  useEffect(() => {
    reloadInitialFiles();
  }, []);

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files]
  );

  return (
    <Container id="fileUploader" fluid={true}>
      <Row>
        <Col className="pr-0 pl-0">
          <ImagePreviewFx
            imageLargeUrl={previewUrl}
            isOpen={isPreviewOpen}
            closePreview={closePreview}
            showRotate={true}
            alternateText={"Image Preview"}
          />
          <StyledContainer
            {...getRootProps({
              isDragActive,
              isDragAccept,
              isDragReject,
            })}
            validForReject={rejectedFiles.length > 0}
            className="mb-2"
          >
            <input {...getInputProps()} />
            <DragMessage
              isDragActive={isDragActive}
              isDragReject={isDragReject}
              rejected={rejectedFiles.length > 0}
            />
            <FileTooLarge />
          </StyledContainer>
          {isImageMode() ? (
            <aside className="thumbsContainer">{imageListing}</aside>
          ) : (
            <Row className="thumbsContainer">{fileListing}</Row>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default FileUploader;

FileUploader.propTypes = {
  minSize: PropTypes.number,
  maxSize: PropTypes.number,
  maxFiles: PropTypes.number,
  mode: PropTypes.string, //Note: Mode can be Image or File, each mode has different layout and behavior
  fileTypes: PropTypes.array,
  initialFiles: PropTypes.array,
  isReadonly: PropTypes.bool,
  cloudStoragePath: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

FileUploader.defaultProps = {
  minSize: 0,
  maxSize: 100000, // 100kb default
  maxFiles: 5,
  mode: "File",
  fileTypes: [],
  initialFiles: [],
  cloudStoragePath: "",
  isReadonly: false,
};
